// 4. 10. 2026 (package 3, G, 8.c): the Veteran Ledger, a goal after level 20 for one season. It opens on level 20
// (FEATURE_UNLOCKS, a toast and the New mark; Survivor → Ledger). Marks come from what a veteran does anyway: one for
// every Survivor Rank reached this season, one for every achievement earned this season and one for every Collection
// book milestone (every 25 fields) reached this season. Nine nodes of 1–3 levels, 1 / 2 / 3 Marks a level, none of
// them touching energy, Power or PvP: the crew, Tobias, repairs, secret keys, Storage, the Workshop queue, crafting time,
// dismantling and selling. The Ledger resets with the season's wipe; the Survivor Rank stays. The first 5 ranks of a
// season take 6,000 XP instead of 9,000 (`rank.ts`), so the first Marks come soon after level 20.

import { GAME } from './constants';

export const LEDGER_RULES = {
  /** False (simulations, `useRulesBefore4Oct`): no Ledger, and every rank takes 9,000 XP. */
  on: true,
  level: GAME.LEVEL_CAP,
  /** Marks a node level costs: the first, the second, the third. */
  cost: [1, 2, 3] as const,
  /** A Collection book milestone: every this many fields. */
  bookStep: 25,
};

export type LedgerNodeId = 'scouts' | 'trader' | 'repairs' | 'keys' | 'storage' | 'bench' | 'craftTime' | 'salvage' | 'haggle';

export interface LedgerNode {
  id: LedgerNodeId;
  name: string;
  /** What one level does. */
  text: string;
  max: 1 | 2 | 3;
  /** The effect of one level (loops, offers, shares). */
  per: number;
}

export const LEDGER_NODES: LedgerNode[] = [
  { id: 'scouts', name: 'Long Haul', text: 'Your scouts can queue 1 more loop.', max: 3, per: 1 },
  { id: 'trader', name: 'Regular', text: 'Tobias shows 1 more offer a day.', max: 2, per: 1 },
  { id: 'repairs', name: 'Patchwork', text: 'Repairs cost 10% less.', max: 3, per: 0.1 },
  { id: 'keys', name: 'Keymaker', text: 'Keys to the secret zones turn up 5% more often.', max: 3, per: 0.05 },
  { id: 'storage', name: 'Deep Storage', text: 'Storage holds 5% more.', max: 3, per: 0.05 },
  { id: 'bench', name: 'Second Bench', text: '1 more place in the Workshop queue.', max: 1, per: 1 },
  { id: 'craftTime', name: 'Quick Hands', text: 'Crafting takes 5% less time.', max: 3, per: 0.05 },
  { id: 'salvage', name: 'Salvager', text: 'Dismantling gives 5% more materials.', max: 3, per: 0.05 },
  { id: 'haggle', name: 'Haggler', text: 'Tobias pays 5% more for what you sell.', max: 2, per: 0.05 },
];

export const LEDGER_NODE = Object.fromEntries(LEDGER_NODES.map((n) => [n.id, n])) as Record<LedgerNodeId, LedgerNode>;

/** The survivor's Ledger: node levels and the season they were bought in (a new season starts it empty). */
export interface LedgerState {
  season: number;
  nodes: Partial<Record<LedgerNodeId, number>>;
}

export const emptyLedger = (season: number): LedgerState => ({ season, nodes: {} });

/** Marks the next level of a node costs, or null at its top. */
export function ledgerNextCost(state: LedgerState | null | undefined, id: LedgerNodeId): number | null {
  const lvl = state?.nodes[id] ?? 0;
  return lvl >= LEDGER_NODE[id].max ? null : LEDGER_RULES.cost[lvl];
}

/** Marks spent on the bought levels. */
export function ledgerSpent(state: LedgerState | null | undefined): number {
  let n = 0;
  for (const [id, lvl] of Object.entries(state?.nodes ?? {}) as [LedgerNodeId, number][]) for (let i = 0; i < Math.min(lvl, LEDGER_NODE[id]?.max ?? 0); i++) n += LEDGER_RULES.cost[i];
  return n;
}

/** Every Mark the Ledger could take (all nodes at the top). */
export const LEDGER_TOTAL = LEDGER_NODES.reduce((a, n) => a + LEDGER_RULES.cost.slice(0, n.max).reduce((x, y) => x + y, 0), 0);

/** Marks earned this season: ranks, achievements and book milestones of the season. */
export function ledgerMarks(o: { ranks: number; achievements: number; bookNow: number; bookAtStart: number }): number {
  const step = LEDGER_RULES.bookStep;
  const milestones = Math.max(0, Math.floor(o.bookNow / step) - Math.floor(o.bookAtStart / step));
  return Math.max(0, o.ranks) + Math.max(0, o.achievements) + milestones;
}

/** The effect of a node at its bought level (0 when not bought or the Ledger is off). */
export function ledgerEffect(state: LedgerState | null | undefined, id: LedgerNodeId): number {
  if (!LEDGER_RULES.on) return 0;
  return (state?.nodes[id] ?? 0) * LEDGER_NODE[id].per;
}
